import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormInput,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { ISettingsGeneral } from "../../../../../models/settings";
import SettingsService from "../../../../../services/settings.service";
import SettingsPageContainer from "../../../../../components/Settings/SettingsPageContainer";
import { useAppSelector } from "../../../../../store/store";
import {
  TSettingsCorreiosForm,
  settingsCorreiosSchema,
} from "./SettingsCorreiosSchema";

const translationPath = "components.settingsGeneral.";

const service = new SettingsService();

const SettingsCorreios = () => {
  const { t } = useTranslation();
  const [userSettings, setUserSettings] = useState<ISettingsGeneral>();
  const methods = useForm<TSettingsCorreiosForm>({
    resolver: zodResolver(settingsCorreiosSchema),
  });
  const { setMessage } = useNotificationContext();
  const { sendRequest } = useFetch(service.getSettingsGeneral);
  const { sendRequest: update, loading } = useFetch(
    service.updateSettingsGeneral
  );
  const generalSettings = useAppSelector(
    (state) => state.settingsGeneral.settings
  );

  const onLoadSettingsGeneral = useCallback(async () => {
    const { data } = await sendRequest(undefined);
    if (data) {
      setUserSettings(data);
    }
  }, [sendRequest]);

  useEffect(() => {
    onLoadSettingsGeneral();
  }, [onLoadSettingsGeneral]);

  useEffect(() => {
    if (userSettings) {
      methods.reset({
        ...userSettings,
        cepCacheTime: userSettings.cepCacheTime.toString(),
        correiosPageSize: userSettings.correiosPageSize.toString(),
      });
    }
  }, [userSettings, methods]);

  const onSubmitHandler = useCallback(
    async (values: TSettingsCorreiosForm) => {
      const { success } = await update({
        item: {
          ...values,
          cepCacheTime: Number(values.cepCacheTime),
          correiosPageSize: Number(values.correiosPageSize),
          apiGeocodingKey: generalSettings.apiGeocodingKey,
          bankBancosUrl: generalSettings.bankBancosUrl,
          bankBaseUrl: generalSettings.bankBancosUrl,
          bankCooperativasUrl: generalSettings.bankCooperativasUrl,
        },
      });
      if (success) {
        setMessage({
          message: `${t(translationPath + "success")}`,
          type: "success",
          notificationKey: new Date().getMilliseconds(),
        });
      }
    },
    [update, setMessage, t, generalSettings]
  );

  const { handleSubmit } = methods;

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <SettingsPageContainer title="Configurações correios">
      <Form {...methods}>
        <Box
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmitHandler)}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: matches ? "100%" : "70%",
            mt: 2,
          }}
        >
          <Grid container columnSpacing={2} mt={2}>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosUrl"
                autoFocus
                fullWidth
                label={t(translationPath + "correios_url")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosTokenUser"
                fullWidth
                label={t(translationPath + "correios_token_user")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosTokenAccessCode"
                fullWidth
                label={t(translationPath + "access_token")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosTokenPostCard"
                fullWidth
                label={t(translationPath + "post_card")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosAuthenticationUrl"
                fullWidth
                label={t(translationPath + "post_card")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosLocationsUrl"
                fullWidth
                label={t(translationPath + "correios_locations")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <FormInput
                size="small"
                name="correiosAddressesUrl"
                fullWidth
                label={t(translationPath + "correios_addresses")}
              />
            </Grid>
            <Grid item xs={12} sm={3} mb={2}>
              <FormInput
                size="small"
                name="cepCacheTime"
                type="number"
                fullWidth
                label={t(translationPath + "cep_cache_time")}
              />
            </Grid>
            <Grid item xs={12} sm={3} mb={2}>
              <FormInput
                size="small"
                name="correiosPageSize"
                type="number"
                fullWidth
                label={t(translationPath + "correios_page_size")}
              />
            </Grid>
            <Grid item xs={12} sm={6} mb={2}>
              <Button
                variant="contained"
                disabled={loading ? true : false}
                type="submit"
                size="small"
              >
                {loading && (
                  <Stack
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    gap={2}
                  >
                    <CircularProgress size={18} />
                    {t(translationPath + "saving")}
                  </Stack>
                )}
                {!loading && t(translationPath + "save")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Form>
    </SettingsPageContainer>
  );
};

export default SettingsCorreios;
